import React, { useMemo } from 'react';
import { useResetAsOfDateOnSubjectChange } from 'venn-state';

import { autoGroupColumnDefStaticConstants, BOLD_CLASS } from '../../../studio-blocks';
import { useIsBlockGridCompact } from '../../../studio-blocks/logic/useIsBlockGridCompact';
import { useCommonTreeGridProps } from '../../../studio-blocks/logic/columnUtils';
import type { AllocationRowData } from '../../../studio-blocks/logic/usePortfolioComparisonUtils';
import ValueCellRenderer from '../../../studio-blocks/components/grid/renderers/ValueCellRenderer';
import { getDefaultValueFormat } from '../../../studio-blocks/logic/gridStyling';
import type { CellClassParams, ColDef, ColGroupDef } from 'ag-grid-community';
import { usePortfolioComparison } from '../../../studio-blocks/logic/usePortfolioComparison';
import { usePortfolioGridDisplaySettings } from '../../../studio-blocks/logic/usePortfolioGridDisplaySettings';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import { useLabelColumnWidth } from '../../../studio-blocks/logic/useLabelColumnMaxWidth';

interface PortfolioComparisonContentProps {
  id: string;
}

const useAutoGroupColumnDef = (columnDefs: ColGroupDef<AllocationRowData>[]): ColDef => {
  const { maxWidth, minWidth } = useLabelColumnWidth(columnDefs);

  return useMemo(
    () => ({
      ...autoGroupColumnDefStaticConstants,
      autoHeight: true,
      minWidth,
      maxWidth,
      cellRendererParams: {
        ...autoGroupColumnDefStaticConstants.cellRendererParams,
        innerRendererParams: { editable: true },
      },
    }),
    [maxWidth, minWidth],
  );
};

const treeGridOverrides: ColDef<AllocationRowData> = {
  cellRenderer: ValueCellRenderer,
  valueFormatter: getDefaultValueFormat,
  cellClassRules: {
    [BOLD_CLASS]: (params: CellClassParams<AllocationRowData>) => !!params.data?.isStrategy,
  },
};

export const PortfolioComparisonContent = ({ id }: PortfolioComparisonContentProps) => {
  const isCompact = useIsBlockGridCompact();

  const commonGridProps = useCommonTreeGridProps(treeGridOverrides);

  const { rowData, columnDefs, onCellValueChanged, pinnedBottomRowData } = usePortfolioComparison(id);
  const shouldExpand = usePortfolioGridDisplaySettings(rowData, columnDefs);
  const autoGroupColumnDef = useAutoGroupColumnDef(columnDefs);

  useResetAsOfDateOnSubjectChange({ blockId: id, numSubjects: 2 });

  return (
    <ExportableGrid
      {...commonGridProps}
      exportable
      autoGroupColumnDef={autoGroupColumnDef}
      selectedRefId={id}
      isCompact={isCompact}
      rowData={rowData}
      columnDefs={columnDefs}
      shouldExpand={shouldExpand}
      onCellValueChanged={onCellValueChanged}
      pinnedBottomRowData={pinnedBottomRowData}
      treeData
    />
  );
};
